import { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "wouter";
import { motion, AnimatePresence } from "framer-motion";
import { useQuizSocket, type LeaderboardEntry, type QuizTeam } from "@/hooks/useQuizSocket";

// ─── Types ────────────────────────────────────────────────────────────────────

interface SessionInfo {
  id: number;
  quizId: number;
  joinCode: string;
  status: string;
  currentRoundId: number | null;
  currentQuestionId: number | null;
  questionStatus: string;
  timerStartedAt: string | null;
  timerDurationSeconds: number | null;
  timerPausedRemainingMs: number | null;
  leaderboardVisible: boolean;
  quiz: {
    id: number;
    title: string;
    rounds: Array<{
      id: number;
      title: string;
      roundNumber: number;
      description: string | null;
      questions: Array<{
        id: number;
        questionNumber: number;
        type: string;
        questionText: string;
        imageUrl: string | null;
        points: number;
        timerSeconds: number | null;
        noTimer: boolean;
        options?: Array<{ id: number; optionText: string; isCorrect: boolean; sortOrder: number }>;
      }>;
    }>;
  };
  teams: QuizTeam[];
}

// ─── Timer bar ────────────────────────────────────────────────────────────────

function TimerBar({
  timerStartedAt,
  durationSeconds,
  pausedRemainingMs,
}: {
  timerStartedAt: string | null;
  durationSeconds: number | null;
  pausedRemainingMs: number | null;
}) {
  const [pct, setPct] = useState(100);
  const [remaining, setRemaining] = useState<number | null>(null);

  useEffect(() => {
    if (!timerStartedAt || !durationSeconds) { setPct(100); setRemaining(null); return; }
    if (pausedRemainingMs !== null) {
      const rem = Math.ceil(pausedRemainingMs / 1000);
      setRemaining(rem);
      setPct((pausedRemainingMs / (durationSeconds * 1000)) * 100);
      return;
    }

    function calc() {
      if (!timerStartedAt || !durationSeconds) return;
      const elapsed = Date.now() - new Date(timerStartedAt).getTime();
      const rem = Math.max(0, durationSeconds * 1000 - elapsed);
      setRemaining(Math.ceil(rem / 1000));
      setPct((rem / (durationSeconds * 1000)) * 100);
    }
    calc();
    const id = setInterval(calc, 100);
    return () => clearInterval(id);
  }, [timerStartedAt, durationSeconds, pausedRemainingMs]);

  if (remaining === null) return null;
  const danger = pct < 25;

  return (
    <div className="space-y-2">
      <div className={`text-6xl font-mono font-bold text-center ${danger ? "text-red-400" : "text-white"}`}>
        {remaining}
      </div>
      <div className="w-full bg-slate-700 rounded-full h-4">
        <motion.div
          className={`h-4 rounded-full ${danger ? "bg-red-500" : "bg-green-400"}`}
          style={{ width: `${pct}%` }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.1, ease: "linear" }}
        />
      </div>
    </div>
  );
}

// ─── Screens ──────────────────────────────────────────────────────────────────

function LobbyScreen({ session, teams }: { session: SessionInfo; teams: QuizTeam[] }) {
  return (
    <div className="flex flex-col items-center justify-center h-full space-y-12 px-8">
      <motion.div
        initial={{ opacity: 0, y: -40 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center"
      >
        <h1 className="text-6xl font-extrabold text-white tracking-tight">
          AYRSHIRE HUB
        </h1>
        <h2 className="text-5xl font-extrabold text-yellow-400 mt-2">
          QUIZ NIGHT
        </h2>
        <p className="text-2xl text-slate-300 mt-4">{session.quiz.title}</p>
      </motion.div>

      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="bg-slate-800 rounded-3xl px-16 py-8 text-center border-4 border-yellow-400"
      >
        <p className="text-2xl text-slate-400 font-semibold uppercase tracking-widest">Join Code</p>
        <p className="text-8xl font-mono font-extrabold text-yellow-400 tracking-widest">
          {session.joinCode}
        </p>
      </motion.div>

      <div className="w-full max-w-4xl">
        <p className="text-xl text-slate-400 text-center mb-4">
          {teams.length} team{teams.length !== 1 ? "s" : ""} ready
        </p>
        <div className="flex flex-wrap gap-3 justify-center">
          <AnimatePresence>
            {teams.map(team => (
              <motion.div
                key={team.id}
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0, opacity: 0 }}
                className="bg-slate-700 rounded-xl px-5 py-3 flex items-center gap-3"
              >
                <span className="text-3xl">{team.emoji ?? "🎯"}</span>
                <span className="text-lg font-bold text-white">{team.name}</span>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

function RoundIntroScreen({ round }: { round: SessionInfo["quiz"]["rounds"][0] }) {
  return (
    <div className="flex flex-col items-center justify-center h-full space-y-8">
      <motion.div
        initial={{ opacity: 0, scale: 0.7 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ type: "spring", bounce: 0.4 }}
        className="text-center"
      >
        <p className="text-4xl font-bold text-yellow-400 uppercase tracking-widest">
          ROUND {round.roundNumber}
        </p>
        <h1 className="text-7xl font-extrabold text-white mt-4">{round.title}</h1>
        {round.description && (
          <p className="text-2xl text-slate-300 mt-6">{round.description}</p>
        )}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="text-3xl text-slate-400 mt-8"
        >
          Get ready...
        </motion.p>
      </motion.div>
    </div>
  );
}

function QuestionScreen({
  question,
  questionNumber,
  totalQuestions,
  roundTitle,
  session,
  answeredCount,
  totalTeams,
}: {
  question: SessionInfo["quiz"]["rounds"][0]["questions"][0];
  questionNumber: number;
  totalQuestions: number;
  roundTitle: string;
  session: SessionInfo;
  answeredCount: number;
  totalTeams: number;
}) {
  const LETTERS = ["A", "B", "C", "D", "E", "F"];
  const options = question.options?.sort((a, b) => a.sortOrder - b.sortOrder) ?? [];
  const isTimerRunning = session.questionStatus === "timer_running" || session.questionStatus === "timer_paused";

  return (
    <div className="flex flex-col h-full p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <span className="text-xl text-slate-400 font-semibold">{roundTitle}</span>
        <span className="text-xl text-slate-400">
          Q{questionNumber} / {totalQuestions}
        </span>
        {totalTeams > 0 && (
          <span className="text-xl text-slate-300">
            <span className="font-bold text-white">{answeredCount}</span>
            <span className="text-slate-400"> / {totalTeams} answered</span>
          </span>
        )}
      </div>

      {/* Question text */}
      <motion.div
        key={question.id}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex-1 flex flex-col items-center justify-center"
      >
        {question.imageUrl && question.type !== "multiple_choice" ? (
          <div className="flex flex-col lg:flex-row items-center gap-8 w-full">
            <img
              src={question.imageUrl}
              alt=""
              className="max-h-64 max-w-lg object-contain rounded-2xl border-4 border-slate-700"
            />
            <p className="text-4xl font-bold text-white text-center flex-1">{question.questionText}</p>
          </div>
        ) : (
          <p className="text-5xl font-bold text-white text-center leading-tight">{question.questionText}</p>
        )}

        {/* Multiple choice options */}
        {options.length > 0 && (
          <div className="grid grid-cols-2 gap-4 w-full mt-8 max-w-4xl">
            {options.map((opt, i) => (
              <motion.div
                key={opt.id}
                initial={{ opacity: 0, x: i % 2 === 0 ? -30 : 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 + i * 0.05 }}
                className="bg-slate-700 rounded-2xl px-6 py-4 flex items-center gap-4"
              >
                <span className="text-3xl font-extrabold text-yellow-400 w-10 shrink-0">{LETTERS[i]}</span>
                <span className="text-2xl font-semibold text-white">{opt.optionText}</span>
              </motion.div>
            ))}
          </div>
        )}
      </motion.div>

      {/* Timer */}
      {isTimerRunning && (
        <div className="max-w-lg mx-auto w-full">
          <TimerBar
            timerStartedAt={session.timerStartedAt}
            durationSeconds={session.timerDurationSeconds}
            pausedRemainingMs={session.timerPausedRemainingMs}
          />
        </div>
      )}
    </div>
  );
}

function AnswerRevealScreen({
  question,
  correctAnswer,
}: {
  question: SessionInfo["quiz"]["rounds"][0]["questions"][0];
  correctAnswer: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center h-full space-y-8 px-8">
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="text-3xl text-slate-400 font-semibold uppercase tracking-widest"
      >
        The answer was...
      </motion.p>
      <motion.div
        initial={{ scale: 0.5, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", bounce: 0.5, delay: 0.3 }}
        className="bg-green-600 rounded-3xl px-16 py-10 text-center"
      >
        <p className="text-7xl font-extrabold text-white">{correctAnswer}</p>
      </motion.div>
      <p className="text-3xl text-slate-300 text-center">{question.questionText}</p>
    </div>
  );
}

function LeaderboardScreen({ rankings }: { rankings: LeaderboardEntry[] }) {
  const top = rankings.slice(0, 10);
  return (
    <div className="flex flex-col items-center justify-center h-full px-8 space-y-6">
      <motion.h2
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-5xl font-extrabold text-yellow-400 uppercase tracking-widest"
      >
        Leaderboard
      </motion.h2>
      <div className="w-full max-w-3xl space-y-2">
        <AnimatePresence>
          {top.map((entry, i) => (
            <motion.div
              key={entry.teamId}
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.08 }}
              className={`flex items-center gap-4 rounded-2xl px-6 py-4 ${
                entry.rank === 1 ? "bg-yellow-500 text-black" :
                entry.rank === 2 ? "bg-slate-300 text-black" :
                entry.rank === 3 ? "bg-amber-700 text-white" :
                "bg-slate-700 text-white"
              }`}
            >
              <span className="text-3xl font-extrabold w-12 text-center">
                {entry.rank === 1 ? "🥇" : entry.rank === 2 ? "🥈" : entry.rank === 3 ? "🥉" : entry.rank}
              </span>
              <span className="text-3xl">{entry.emoji ?? ""}</span>
              <span className="flex-1 text-2xl font-bold">{entry.name}</span>
              {entry.change !== undefined && entry.change !== 0 && (
                <span className={`text-lg font-bold ${entry.change > 0 ? "text-green-300" : "text-red-300"}`}>
                  {entry.change > 0 ? "↑" : "↓"}{Math.abs(entry.change)}
                </span>
              )}
              <span className="text-3xl font-extrabold">{entry.score}</span>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
}

function WinnerScreen({ rankings }: { rankings: LeaderboardEntry[] }) {
  const winner = rankings[0];
  const second = rankings[1];
  const third = rankings[2];

  // CSS confetti
  const confettiRef = useRef<HTMLDivElement>(null);

  return (
    <div className="flex flex-col items-center justify-center h-full space-y-8 relative overflow-hidden">
      {/* Confetti */}
      <div ref={confettiRef} className="absolute inset-0 pointer-events-none">
        {Array.from({ length: 60 }).map((_, i) => (
          <div
            key={i}
            className="absolute w-3 h-3 rounded-sm opacity-90"
            style={{
              left: `${Math.random() * 100}%`,
              top: `-${Math.random() * 20}%`,
              backgroundColor: ["#fbbf24", "#34d399", "#60a5fa", "#f472b6", "#a78bfa"][i % 5],
              animation: `fall ${2 + Math.random() * 3}s linear ${Math.random() * 2}s infinite`,
              transform: `rotate(${Math.random() * 360}deg)`,
            }}
          />
        ))}
      </div>

      <style>{`
        @keyframes fall {
          0% { transform: translateY(-20px) rotate(0deg); opacity: 1; }
          100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
        }
      `}</style>

      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", bounce: 0.6 }}
        className="text-center"
      >
        <p className="text-4xl font-bold text-yellow-400 uppercase tracking-widest mb-2">
          🏆 Quiz Champions 🏆
        </p>
        {winner && (
          <>
            <p className="text-4xl text-slate-300 mb-2">AYRSHIRE&apos;S QUIZ CHAMPIONS ARE...</p>
            <div className="flex items-center justify-center gap-4 mt-4">
              <span className="text-6xl">{winner.emoji ?? "🎯"}</span>
              <p className="text-8xl font-extrabold text-yellow-400">{winner.name}</p>
            </div>
            <p className="text-4xl text-slate-300 mt-4">{winner.score} points</p>
          </>
        )}
      </motion.div>

      {/* 2nd and 3rd */}
      <div className="flex gap-8 flex-wrap justify-center">
        {second && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-slate-300 text-black rounded-2xl px-8 py-4 text-center"
          >
            <p className="text-2xl">🥈</p>
            <p className="text-2xl font-bold">{second.name}</p>
            <p className="text-lg">{second.score} pts</p>
          </motion.div>
        )}
        {third && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="bg-amber-700 text-white rounded-2xl px-8 py-4 text-center"
          >
            <p className="text-2xl">🥉</p>
            <p className="text-2xl font-bold">{third.name}</p>
            <p className="text-lg">{third.score} pts</p>
          </motion.div>
        )}
      </div>
    </div>
  );
}

// ─── Main projector ───────────────────────────────────────────────────────────

export default function AdminQuizProjector() {
  const params = useParams<{ sessionId: string }>();
  const sessionId = Number(params.sessionId);
  const token = localStorage.getItem("ayrshire_token");

  const { state, connected } = useQuizSocket(isNaN(sessionId) ? null : sessionId);

  const { data: session } = useQuery<SessionInfo>({
    queryKey: ["quiz-session-projector", sessionId],
    enabled: !isNaN(sessionId),
    refetchInterval: connected ? 30_000 : 3_000,
    queryFn: () =>
      fetch(`/api/quiz/sessions/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then(r => r.json()),
  });

  // Auto-fullscreen on mount
  useEffect(() => {
    document.documentElement.requestFullscreen().catch(() => null);
    return () => {
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => null);
      }
    };
  }, []);

  const liveSession = state.session;
  const teams = state.teams.length > 0 ? state.teams : (session?.teams ?? []);
  const leaderboard = state.leaderboard.length > 0 ? state.leaderboard : [];

  const effectiveStatus = liveSession?.status ?? session?.status ?? "lobby";
  const questionStatus = liveSession?.questionStatus ?? session?.questionStatus ?? "idle";
  const currentRoundId = liveSession?.currentRoundId ?? session?.currentRoundId;
  const currentQuestionId = liveSession?.currentQuestionId ?? session?.currentQuestionId;

  const currentRound = session?.quiz.rounds.find(r => r.id === currentRoundId);
  const currentQuestion = currentRound?.questions.find(q => q.id === currentQuestionId);

  // Determine screen
  type Screen = "lobby" | "round_intro" | "question" | "reveal" | "leaderboard" | "winner";

  function currentScreen(): Screen {
    if (effectiveStatus === "completed" && leaderboard.length > 0) return "winner";
    if (effectiveStatus === "lobby" || !currentRoundId) return "lobby";
    if (!currentQuestionId && currentRoundId) return "round_intro";
    if (questionStatus === "revealed") return "reveal";
    if (liveSession?.leaderboardVisible || effectiveStatus === "completed") return "leaderboard";
    return "question";
  }

  const screen = currentScreen();

  const sessionForTimer = {
    questionStatus,
    timerStartedAt: liveSession?.timerStartedAt ?? session?.timerStartedAt ?? null,
    timerDurationSeconds: liveSession?.timerDurationSeconds ?? session?.timerDurationSeconds ?? null,
    timerPausedRemainingMs: liveSession?.timerPausedRemainingMs ?? session?.timerPausedRemainingMs ?? null,
  };

  return (
    <div className="min-h-screen bg-slate-900 overflow-hidden" style={{ fontFamily: "system-ui, sans-serif" }}>
      {/* Connection indicator */}
      <div className={`fixed top-3 right-3 w-3 h-3 rounded-full z-50 ${connected ? "bg-green-500" : "bg-red-500"}`} />

      <div className="h-screen flex flex-col">
        <AnimatePresence mode="wait">
          {screen === "lobby" && session && (
            <motion.div key="lobby" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex-1">
              <LobbyScreen session={session} teams={teams} />
            </motion.div>
          )}

          {screen === "round_intro" && currentRound && (
            <motion.div key={`round-${currentRound.id}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex-1">
              <RoundIntroScreen round={currentRound} />
            </motion.div>
          )}

          {screen === "question" && currentQuestion && currentRound && (
            <motion.div key={`question-${currentQuestion.id}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex-1">
              <QuestionScreen
                question={currentQuestion}
                questionNumber={currentQuestion.questionNumber}
                totalQuestions={currentRound.questions.length}
                roundTitle={currentRound.title}
                session={sessionForTimer as unknown as SessionInfo}
                answeredCount={state.answeredCount}
                totalTeams={state.totalTeams > 0 ? state.totalTeams : teams.length}
              />
            </motion.div>
          )}

          {screen === "reveal" && currentQuestion && (
            <motion.div key={`reveal-${currentQuestion.id}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex-1">
              <AnswerRevealScreen
                question={currentQuestion}
                correctAnswer={"Answer revealed"}
              />
            </motion.div>
          )}

          {screen === "leaderboard" && (
            <motion.div key="leaderboard" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex-1">
              <LeaderboardScreen rankings={leaderboard} />
            </motion.div>
          )}

          {screen === "winner" && (
            <motion.div key="winner" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex-1">
              <WinnerScreen rankings={leaderboard} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
